import { supabase } from '@/lib/supabaseClient';
import {
  deriveInvoiceStatus,
  parseContractorDate,
  updateJob,
  formatUsd,
  computeEmbeddedJobMargin,
} from '@/lib/jobsData';
import * as pqi from '@/lib/pqiTracker';

export { formatUsd };

/* ----------------------------------------------------------------------------
 * Fetch jobs for payments overview
 * ------------------------------------------------------------------------- */
const JOB_FIELDS =
  'id,name,client,client_id,address,contract,co_total,status,phase,invoices,created_at';

export const fetchPaymentsData = async () => {
  const { data, error } = await supabase
    .from('jobs')
    .select(JOB_FIELDS)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

/* ----------------------------------------------------------------------------
 * Per-invoice flatten (reuses existing logic)
 * ------------------------------------------------------------------------- */
export const flattenPayments = (jobs = []) => {
  const rows = [];
  for (const job of jobs) {
    const invoices = Array.isArray(job?.invoices) ? job.invoices : [];
    invoices.forEach((inv, idx) => {
      const status = deriveInvoiceStatus(inv);
      const dueDate = parseContractorDate(inv?.due);
      let daysPastDue = null;
      let daysUntilDue = null;
      if (dueDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diff = Math.round((dueDate - today) / (1000 * 60 * 60 * 24));
        if (diff < 0) daysPastDue = Math.abs(diff);
        else daysUntilDue = diff;
      }
      rows.push({
        row_key: `${job.id}-${inv?.id || 'noid'}-${idx}`,
        invoice_id: inv?.id || null,
        idx,
        job_id: job.id,
        job_name: job.name || 'Untitled job',
        client: job.client || '',
        client_id: job.client_id || null,
        amount: Number(inv?.amount) || 0,
        desc: inv?.desc || '',
        due: inv?.due || '',
        due_date: dueDate,
        status,
        days_past_due: daysPastDue,
        days_until_due: daysUntilDue,
        contract: Number(job.contract) || 0,
      });
    });
  }
  return rows;
};

/* ----------------------------------------------------------------------------
 * Per-job aggregation
 * ------------------------------------------------------------------------- */
export const buildPaymentsByJob = (jobs = []) => {
  return jobs.map((job) => {
    const invoices = Array.isArray(job?.invoices) ? job.invoices : [];
    let totalInvoiced = 0;
    let collected = 0;
    let outstanding = 0;
    let overdue = 0;
    let dueSoon = 0;
    let upcoming = 0;
    const counts = { paid: 0, overdue: 0, due: 0, upcoming: 0 };
    for (const inv of invoices) {
      const amount = Number(inv?.amount) || 0;
      const status = deriveInvoiceStatus(inv);
      totalInvoiced += amount;
      if (status === 'paid') {
        collected += amount;
        counts.paid += 1;
      } else {
        outstanding += amount;
        if (status === 'overdue') {
          overdue += amount;
          counts.overdue += 1;
        } else if (status === 'due') {
          dueSoon += amount;
          counts.due += 1;
        } else {
          upcoming += amount;
          counts.upcoming += 1;
        }
      }
    }
    const contract = Number(job.contract) || 0;
    const coTotal = Number(job.co_total) || 0;
    const pipeline = contract + coTotal;
    const collectedPct =
      totalInvoiced > 0 ? Math.round((collected / totalInvoiced) * 100) : 0;
    return {
      job,
      job_id: job.id,
      job_name: job.name || 'Untitled job',
      client: job.client || '',
      address: job.address || '',
      status: job.status || '',
      contract,
      coTotal,
      pipeline,
      totalInvoiced,
      collected,
      outstanding,
      overdue,
      dueSoon,
      upcoming,
      collectedPct,
      counts,
      invoiceCount: invoices.length,
    };
  });
};

/* ----------------------------------------------------------------------------
 * Top-level stats
 * ------------------------------------------------------------------------- */
export const computePaymentStats = (rows = [], byJob = []) => {
  let totalInvoiced = 0;
  let collected = 0;
  let outstanding = 0;
  let overdue = 0;
  let pipeline = 0;
  let outstandingCount = 0;
  let overdueCount = 0;
  let collectedCount = 0;
  for (const r of rows) {
    totalInvoiced += r.amount;
    if (r.status === 'paid') {
      collected += r.amount;
      collectedCount += 1;
    } else {
      outstanding += r.amount;
      outstandingCount += 1;
      if (r.status === 'overdue') {
        overdue += r.amount;
        overdueCount += 1;
      }
    }
  }
  for (const j of byJob) {
    pipeline += j.pipeline;
  }
  const collectedPct =
    totalInvoiced > 0 ? Math.round((collected / totalInvoiced) * 100) : 0;
  return {
    totalInvoiced,
    collected,
    outstanding,
    overdue,
    pipeline,
    collectedPct,
    counts: {
      total: rows.length,
      paid: collectedCount,
      outstanding: outstandingCount,
      overdue: overdueCount,
      jobs: byJob.length,
    },
  };
};

/* ----------------------------------------------------------------------------
 * Filter
 * ------------------------------------------------------------------------- */
export const applyPaymentFilter = (
  rows = [],
  { search = '', view = 'all' } = {}
) => {
  const q = (search || '').trim().toLowerCase();
  return rows.filter((r) => {
    if (view === 'collected' && r.status !== 'paid') return false;
    if (view === 'outstanding' && r.status === 'paid') return false;
    if (view === 'overdue' && r.status !== 'overdue') return false;
    if (view === 'pipeline' && r.status === 'paid') return false;
    if (!q) return true;
    const hay = [r.job_name, r.client, r.desc]
      .map((v) => (v || '').toLowerCase())
      .join(' | ');
    return hay.includes(q);
  });
};

/* ----------------------------------------------------------------------------
 * Mark paid helper
 * ------------------------------------------------------------------------- */
export const markInvoicePaidById = async (job, invoiceId) => {
  if (!job?.id) throw new Error('Missing job id');
  const inv = (Array.isArray(job.invoices) ? job.invoices : []).find(
    (i) => i?.id === invoiceId
  );
  const invoices = (Array.isArray(job.invoices) ? job.invoices : []).map((inv2) =>
    inv2?.id === invoiceId ? { ...inv2, status: 'paid', paid_at: new Date().toISOString() } : inv2
  );
  const updated = await updateJob(job.id, { invoices });
  // PQI: track invoice payment (silent)
  pqi.trackInvoicePaid({ job, invoiceId, amount: Number(inv?.amount) || 0 });
  // PQI: margin snapshot — revenue collected posture changed
  {
    const { actualCost, totalRevenue, marginPct } = computeEmbeddedJobMargin(updated);
    pqi.trackMarginSnapshot({
      job: updated,
      actualCost,
      totalRevenue,
      marginPct,
      trigger: 'invoice_paid',
    });
  }
  return updated;
};

/* ----------------------------------------------------------------------------
 * CSV export
 * ------------------------------------------------------------------------- */
export const paymentsToCsv = (rows = []) => {
  const header = [
    'Job',
    'Client',
    'Description',
    'Due',
    'Amount',
    'Status',
    'Days Past Due',
  ];
  const esc = (v) => {
    const s = String(v == null ? '' : v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [header.join(',')];
  for (const r of rows) {
    lines.push(
      [
        r.job_name,
        r.client,
        r.desc,
        r.due,
        r.amount.toFixed(2),
        r.status,
        r.days_past_due ?? '',
      ]
        .map(esc)
        .join(',')
    );
  }
  return lines.join('\n');
};

export const downloadCsv = (filename, csvContent) => {
  try {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    // noop
  }
};
